'use client';

import { useNearViewport } from '@/hooks/use-near-viewport';
import { fetchPublicHomepageSection } from '@/lib/fetch-homepage-section';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { Button } from '../ui/button';
import {
  HomepageReviewsSkeleton,
  HomepageSectionSkeleton,
  HOMEPAGE_LEADING_SPACE,
  HOMEPAGE_TRAILING_SPACE,
} from './section-skeleton';
import {
  HOMEPAGE_SECTION_STALE_MS,
  homepageProductsQuery,
  homepageSectionKey,
  isActiveHomepageDeal,
  type HomepageSection,
  type HomepageSectionResponse,
} from '@/lib/homepage-sections';

// Deferring the request alone still shipped every section's runtime at startup.
// Warm each section's import at its own near-viewport boundary, alongside data.
const loadProducts = () =>
  import('../product/product-container-view').then((m) => ({ default: m.ProductsContainerView }));
const ProductsContainer = lazy(loadProducts);
const loadBlogs = () => import('./blogs').then((m) => ({ default: m.Blogs }));
const loadIcons = () => import('./icons').then((m) => ({ default: m.Icons }));
const loadStores = () => import('./stores').then((m) => ({ default: m.Stores }));
const loadCategories = () =>
  import('./highlighted-categories').then((m) => ({ default: m.HighlightedCategories }));
const loadLimitedProduct = () =>
  import('./limited-product').then((m) => ({ default: m.LimitedProduct }));
const loadWeekProduct = () => import('./week-product').then((m) => ({ default: m.WeekProduct }));
const Blogs = lazy(loadBlogs);
const Icons = lazy(loadIcons);
const Stores = lazy(loadStores);
const HighlightedCategories = lazy(loadCategories);
const LimitedProduct = lazy(loadLimitedProduct);
const WeekProduct = lazy(loadWeekProduct);
const Reviews = lazy(() => import('./reviews').then((m) => ({ default: m.Reviews })));

const sectionLoaders: Record<HomepageSection, () => Promise<unknown>> = {
  'top-products': loadProducts,
  'best-products': loadProducts,
  categories: loadCategories,
  blogs: loadBlogs,
  stores: loadStores,
  icons: loadIcons,
  deals: loadWeekProduct,
};

export function DeferredHomepageSection({
  section,
  locale,
  title = '',
  description = '',
  dealIndex = 0,
}: {
  section: HomepageSection;
  locale: string;
  title?: string;
  description?: string;
  dealIndex?: 0 | 1;
}) {
  // Warm the approaching section's data AND chunk together. Optional deals
  // have no speculative blank height, so resolve their presence further ahead.
  const { ref, enabled } = useNearViewport({
    rootMargin: section === 'deals' ? '1200px 0px' : '320px 0px',
  });
  const { me, isInitialized } = useCustomerStore();
  const { t } = useTranslations();
  const commerce = section === 'top-products' || section === 'best-products' || section === 'deals';
  const query = useQuery({
    queryKey: homepageSectionKey(section, locale, commerce ? me?.customer.id : undefined),
    enabled: enabled && (!commerce || isInitialized),
    staleTime: HOMEPAGE_SECTION_STALE_MS,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<HomepageSectionResponse> => {
      // Private prices never use the anonymous endpoint or another customer's seed.
      if (me?.authToken && (section === 'top-products' || section === 'best-products')) {
        const { fetchProducts } = await import('@eliadmin/core/src/api/products/product-api');
        return {
          section,
          data: (
            await fetchProducts({
              locale,
              authToken: me.authToken,
              query: homepageProductsQuery(section),
              signal,
            })
          ).data,
        };
      }
      if (me?.authToken && section === 'deals') {
        const { fetchDailyDeals } =
          await import('@eliadmin/core/src/api/daily-deals/daily-deals-api');
        return {
          section,
          data: (
            await fetchDailyDeals({
              locale,
              authToken: me.authToken,
              query: { page: 1, results: 2 },
              where: { is_running: { equals: true } },
            })
          ).data,
        };
      }
      return fetchPublicHomepageSection(section, locale, signal);
    },
  });

  const result = query.data;
  useEffect(() => {
    if (!enabled) return;
    // Do not download a promotion's commerce code when no live promotion exists.
    if (
      section === 'deals' &&
      (result?.section !== 'deals' || !isActiveHomepageDeal(result.data[dealIndex]))
    )
      return;
    const load =
      section === 'deals' && dealIndex === 1 ? loadLimitedProduct : sectionLoaders[section];
    // Actual lazy rendering retains error handling; a warm-up failure must not
    // become an unhandled rejection before the section is rendered.
    void load().catch(() => undefined);
  }, [enabled, section, dealIndex, result]);

  let content;
  if (result) {
    switch (result.section) {
      case 'top-products':
      case 'best-products':
        content = result.data.length ? (
          <CarouselProvider>
            <ProductsContainer
              products={result.data}
              title={title}
              description={description}
              variant={result.section === 'top-products' ? 'carousel' : 'grid'}
              className={{
                section:
                  result.section === 'top-products'
                    ? HOMEPAGE_LEADING_SPACE
                    : HOMEPAGE_TRAILING_SPACE,
              }}
            />
          </CarouselProvider>
        ) : null;
        break;
      case 'categories':
        content = <HighlightedCategories categories={result.data} title={title} />;
        break;
      case 'blogs':
        content = <Blogs blogs={result.data} title={title} />;
        break;
      case 'icons':
        content = <Icons icons={result.data} className="py-6 sm:py-8 md:py-10 lg:py-12" />;
        break;
      case 'stores':
        content = <Stores stores={result.data} className={HOMEPAGE_LEADING_SPACE} />;
        break;
      case 'deals': {
        const deal = result.data[dealIndex];
        content =
          deal && isActiveHomepageDeal(deal) ? (
            dealIndex === 1 ? (
              <LimitedProduct data={deal} locale={locale} managedExternally />
            ) : (
              <WeekProduct
                data={deal}
                locale={locale}
                managedExternally
                className={HOMEPAGE_TRAILING_SPACE}
              />
            )
          ) : null;
        break;
      }
    }
  }
  const placeholder = (
    <div className={query.isError && section === 'deals' ? 'relative min-h-12' : 'relative'}>
      <HomepageSectionSkeleton
        section={section}
        title={title}
        description={description}
        result={result}
        dealIndex={dealIndex}
      />
      {query.isError && (
        <div role="alert" className="absolute inset-0 flex items-center justify-center">
          <Button variant="outline" onClick={() => void query.refetch()}>
            {t('text595')}
          </Button>
        </div>
      )}
    </div>
  );
  return (
    <div
      ref={ref}
      data-homepage-section={section}
      aria-busy={!result && !query.isError}
      className={
        section === 'deals' && !result ? (query.isError ? 'min-h-12' : 'min-h-px') : undefined
      }
    >
      {result ? <Suspense fallback={placeholder}>{content}</Suspense> : placeholder}
    </div>
  );
}

export function DeferredHomepageReviews({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const { ref, enabled } = useNearViewport({ rootMargin: '320px 0px' });
  const placeholder = <HomepageReviewsSkeleton title={title} description={description} />;
  return (
    <div ref={ref}>
      {enabled ? (
        <Suspense fallback={placeholder}>
          <Reviews title={title} description={description} />
        </Suspense>
      ) : (
        placeholder
      )}
    </div>
  );
}
